/**
 * ZOY-237/693: the SET of delivery modes a service offers or a practitioner
 * works in during a window — chips that toggle, at least one kept on. The
 * choices can be narrowed (a group service never runs at home).
 */

import { useIntl } from 'react-intl';
import k from '@/ui/ui.module.css';
import { DELIVERY_MODES, type DeliveryMode } from '@/core/clinic/setupApi';

export function ModeSetField(props: {
  label: string;
  value: readonly DeliveryMode[];
  onChange: (v: DeliveryMode[]) => void;
  choices?: readonly DeliveryMode[];
  help?: string;
  error?: string;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const choices = props.choices ?? DELIVERY_MODES;
  const toggle = (m: DeliveryMode) =>
    props.onChange(
      props.value.includes(m)
        ? props.value.filter((x) => x !== m)
        : DELIVERY_MODES.filter((x) => x === m || props.value.includes(x)),
    );
  return (
    <div className={k.field}>
      <span className={k.fieldTop}>
        <span className={k.fieldLabel}>{props.label}</span>
      </span>
      <div
        role="group"
        aria-label={props.label}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
      >
        {choices.map((m) => {
          const on = props.value.includes(m);
          return (
            <button
              key={m}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(m)}
              style={{
                minBlockSize: 40,
                paddingInline: 14,
                borderRadius: 999,
                border: `1.5px solid ${on ? 'var(--zc-sage-dot)' : 'var(--zc-line-strong)'}`,
                background: on ? 'var(--zc-sage-bg)' : 'var(--zc-card)',
                color: on ? 'var(--zc-sage-ink)' : 'var(--zc-ink-2)',
                cursor: 'pointer',
                fontSize: 13.5,
              }}
            >
              {t(`svc.mode.${m}`)}
            </button>
          );
        })}
      </div>
      {props.error ? (
        <span
          className={k.fieldHelp}
          style={{ color: 'var(--zc-danger-ink, #b3261e)' }}
          role="alert"
        >
          {props.error}
        </span>
      ) : (
        <span className={k.fieldHelp}>{props.help ?? ' '}</span>
      )}
    </div>
  );
}

/** Short, joined label for a mode set (list cells and chips). */
export function modesLabel(
  t: (id: string) => React.ReactNode,
  modes: readonly DeliveryMode[],
): string {
  return modes.map((m) => t(`svc.mode.${m}`) as string).join(' · ');
}
